import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { buildCustomerStatementCsv } from '../../application/customerStatementCsv'
import { getCustomerAccountById, getCustomerMovementHistoryForExport, getCustomerSalesForCustomer } from '../../infra/customerRepository'
import { subscribeCustomerSynced } from '../../../sync/core/customerSyncListeners';
import { sendCustomerWhatsAppReminder, shareCustomerPaymentLink } from '../../application/customerPortal'
import type { CustomerAccountRow } from '../../domain/customerTypes';
import { computeAgingBuckets } from '../../application/customerAging';
import type { AgingBuckets } from '../../components/CustomerAgingBreakdown';
import type { CustomerPendingSaleRow } from '../../components/CustomerPendingSalesPanel';
import { shareOrDownloadCsv } from '../../../shared/csv-core/infra/shareCsv';
import { openSaleDetail } from '../../../sales/application/salesNavigation';
import { showError } from '../../../shared/ui/appToast';
import { reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { preserveCustomerDetailForShellNavigation } from '../customerDetailPreserve';
import { useCustomerBlockFlow } from './useCustomerBlockFlow';
import { useCustomerAppointments } from './useCustomerAppointments';
import { useCustomerServiceRecords } from './useCustomerServiceRecords';
import { useCustomerSales } from './useCustomerSales';
import { useCustomerTasks } from './useCustomerTasks';
import { upsertCustomerNote, archiveCustomerNote, upsertCustomerTask, completeCustomerTask, archiveCustomerTask } from '../../infra/customer360Repo';
import type { OperationalConfirmModalProps } from '../../../shared/modals/OperationalConfirmModal';
import type { useCustomerEditorFlow } from './useCustomerEditorFlow';
import type { useCustomerMovementFlow } from './useCustomerMovementFlow';
import type { useCustomerMovementHistoryFlow } from './useCustomerMovementHistoryFlow';
type MovementFlow = ReturnType<typeof useCustomerMovementFlow>;
type HistoryFlow = ReturnType<typeof useCustomerMovementHistoryFlow>;
type EditorFlow = ReturnType<typeof useCustomerEditorFlow>;

type Params = {
  canEditCustomer: boolean;
  canSetCreditLimit: boolean;
  canCollect: boolean;
  canAdjustDebt: boolean;
  canManageTasks: boolean;
  portalBaseUrl: string;
  paymentLinkAllowed: boolean;
  formatMoney: (value: number) => string;
  refreshCustomerTotalCount: () => Promise<void>;
  loadCustomers: () => Promise<void>;
  movementFlow: MovementFlow;
  historyFlow: HistoryFlow;
  editorFlow: EditorFlow;
};

export function useCustomerDetailPane({
  canEditCustomer,
  canSetCreditLimit,
  canCollect,
  canAdjustDebt,
  canManageTasks,
  portalBaseUrl,
  paymentLinkAllowed,
  formatMoney,
  refreshCustomerTotalCount,
  loadCustomers,
  movementFlow,
  historyFlow,
  editorFlow,
}: Params) {
  const navigation = useNavigation();
  const [linkingCustomerId, setLinkingCustomerId] = useState<string | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState<CustomerAccountRow | null>(null);
  const [pendingSales, setPendingSales] = useState<CustomerPendingSaleRow[]>([]);
  const [loadingPendingSales, setLoadingPendingSales] = useState(false);
  const user = useAuthStore((s) => s.user);
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);

  useEffect(() => {
    if (detailVisible && detailCustomer) {
      setLoadingPendingSales(true);
      getCustomerSalesForCustomer(detailCustomer.id)
        .then((sales) => setPendingSales(sales))
        .catch((err) => console.warn('[useCustomerDetailPane] Error loading customer sales', err))
        .finally(() => setLoadingPendingSales(false));
    } else {
      setPendingSales([]);
      setLoadingPendingSales(false);
    }
  }, [detailVisible, detailCustomer]);

  // La ficha lee de un snapshot tomado al abrirla: si un pull trae datos
  // nuevos (cobro POS, devolución/rechazo de cheque), refrescarla para que
  // el saldo no quede viejo en pantalla.
  useEffect(() => {
    if (!detailVisible || !detailCustomer) return;
    const customerId = detailCustomer.id;
    const seenUpdatedAt = detailCustomer.updatedAt;
    const seenBalance = detailCustomer.balance;
    return subscribeCustomerSynced(() => {
      void getCustomerAccountById(customerId).then((fresh) => {
        if (!fresh) return;
        setDetailCustomer((current) =>
          current && current.id === customerId && current.updatedAt === seenUpdatedAt && current.balance === seenBalance
            ? fresh
            : current
        );
      });
    });
  }, [detailVisible, detailCustomer]);

  const agingBuckets = useMemo<AgingBuckets | null>(() => {
    if (!detailCustomer || loadingPendingSales) return null;
    return computeAgingBuckets(
      detailCustomer.balance,
      pendingSales,
      detailCustomer.updatedAt
    );
  }, [detailCustomer, pendingSales, loadingPendingSales]);

  const customerAppointments = useCustomerAppointments(
    detailCustomer?.id ?? null,
    detailVisible,
  );

  const customerServiceRecords = useCustomerServiceRecords(
    detailCustomer?.id ?? null,
    detailVisible,
  );

  const customerSales = useCustomerSales(
    detailCustomer?.id ?? null,
    detailVisible,
  );

  const customerTasks = useCustomerTasks(
    detailCustomer?.id ?? null,
    detailVisible,
  );

  const handleAddNote = useCallback(
    async (body: string, isAlert: boolean) => {
      if (!detailCustomer) return;
      try {
        await upsertCustomerNote({
          customerId: detailCustomer.id,
          body,
          isAlert,
        });
        await customerServiceRecords.reload();
      } catch (error: unknown) {
        reportUserVisibleFailure({
          module: 'customers',
          op: 'customer.note_upsert',
          error,
          title: 'No se pudo guardar la nota.',
        });
        showError('Nota', 'No se pudo guardar la nota.');
        throw error;
      }
    },
    [detailCustomer, customerServiceRecords],
  );

  const [pendingArchiveNoteId, setPendingArchiveNoteId] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);

  const requestArchiveNote = useCallback((noteId: string) => {
    setPendingArchiveNoteId(noteId);
  }, []);

  const closeArchiveConfirm = useCallback(() => {
    if (archiving) return;
    setPendingArchiveNoteId(null);
  }, [archiving]);

  const confirmArchiveNote = useCallback(async () => {
    if (!pendingArchiveNoteId) return;
    setArchiving(true);
    try {
      await archiveCustomerNote(pendingArchiveNoteId);
      await customerServiceRecords.reload();
      setPendingArchiveNoteId(null);
    } catch (error: unknown) {
      reportUserVisibleFailure({
        module: 'customers',
        op: 'customer.note_archive',
        error,
        title: 'No se pudo archivar la nota.',
      });
      showError('Nota', 'No se pudo archivar la nota.');
    } finally {
      setArchiving(false);
    }
  }, [pendingArchiveNoteId, customerServiceRecords]);

  const archiveNoteConfirmModalProps = useMemo(() => {
    if (!pendingArchiveNoteId) return null;
    return {
      title: 'Archivar nota',
      message: '¿Seguro que querés archivar esta nota? Vas a poder verla desde el historial, pero no en la ficha.',
      confirmLabel: archiving ? 'Archivando…' : 'Archivar',
      destructive: true,
      iconName: 'trash-outline' as const,
      loading: archiving,
    } satisfies Omit<OperationalConfirmModalProps, 'visible' | 'presentation' | 'onClose' | 'onConfirm'>;
  }, [pendingArchiveNoteId, archiving]);

  const handleArchiveNote = useCallback(
    (noteId: string) => {
      requestArchiveNote(noteId);
    },
    [requestArchiveNote],
  );

  // ── Tasks handlers ──

  const handleAddTask = useCallback(
    async (title: string, kind: 'call' | 'message' | 'visit' | 'task', dueDate: string | null) => {
      if (!detailCustomer) return;
      try {
        await upsertCustomerTask({
          customerId: detailCustomer.id,
          title,
          kind,
          dueDate,
        });
        await customerTasks.reload();
      } catch (error: unknown) {
        reportUserVisibleFailure({
          module: 'customers',
          op: 'customer.task_upsert',
          error,
          title: 'No se pudo guardar la tarea.',
        });
        showError('Tarea', 'No se pudo guardar la tarea.');
        throw error;
      }
    },
    [detailCustomer, customerTasks],
  );

  const handleCompleteTask = useCallback(
    async (taskId: string) => {
      try {
        await completeCustomerTask(taskId);
        await customerTasks.reload();
      } catch (error: unknown) {
        reportUserVisibleFailure({
          module: 'customers',
          op: 'customer.task_complete',
          error,
          title: 'No se pudo completar la tarea.',
        });
        showError('Tarea', 'No se pudo completar la tarea.');
      }
    },
    [customerTasks],
  );

  const handleArchiveTask = useCallback(
    async (taskId: string) => {
      try {
        await archiveCustomerTask(taskId);
        await customerTasks.reload();
      } catch (error: unknown) {
        reportUserVisibleFailure({
          module: 'customers',
          op: 'customer.task_archive',
          error,
          title: 'No se pudo archivar la tarea.',
        });
        showError('Tarea', 'No se pudo archivar la tarea.');
      }
    },
    [customerTasks],
  );

  const branches = useSessionStore((s) => s.branches);
  const branchOptions = useMemo(
    () => branches.map((b) => ({ id: b.id, name: b.name })),
    [branches],
  );

  const actorLabel = useCallback(
    (uid: string | null | undefined): string => {
      if (!uid) return '—';
      if (user?.id === uid) return user.email ?? uid;
      const emp = employees.find((e) => e.authUserId === uid);
      return emp?.name ?? `${uid.slice(0, 8)}…`;
    },
    [user, employees]
  );

  const exportStatement = useCallback(
    async (customer: CustomerAccountRow) => {
      try {
        await useEmployeesStore.getState().loadEmployees().catch(() => {});
        const rows = await getCustomerMovementHistoryForExport(customer.id);
        const csv = buildCustomerStatementCsv({ customer, rows, actorLabel, formatMoney });
        const safeName = customer.name.replace(/[^\w\-]+/g, '_').slice(0, 40);
        await shareOrDownloadCsv(csv, `estado_cuenta_${safeName}.csv`);
      } catch (e) {
        console.warn('[Customer] export statement', e);
        showError('Exportar', 'No se pudo armar el listado.');
      }
    },
    [actorLabel, formatMoney]
  );

  const canBlock = canEditCustomer;

  const blockFlow = useCustomerBlockFlow({
    canEdit: canBlock,
    refreshCustomerTotalCount,
    loadCustomers,
    detailCustomer,
    setDetailCustomer,
  });

  const openCustomerDetail = useCallback((customer: CustomerAccountRow) => {
    setDetailCustomer(customer);
    setDetailVisible(true);
  }, []);

  const closeCustomerDetail = useCallback(() => {
    setDetailVisible(false);
    setDetailCustomer(null);
  }, []);

  const sharePaymentLink = useCallback(
    async (customer: CustomerAccountRow) => {
      try {
        setLinkingCustomerId(customer.id);
        await shareCustomerPaymentLink({
          customer,
          portalBaseUrl,
          canEdit: canEditCustomer || canCollect || canAdjustDebt,
          paymentLinkAllowed,
        });
        void refreshCustomerTotalCount();
        void loadCustomers();
      } catch (error: unknown) {
        reportUserVisibleFailure({ module: 'customers', op: 'customer.share_link', error, title: 'No se pudo compartir el link de cobro.' });
        console.warn('[Customer] Error al compartir link:', error);
        showError(
          'Link de cobro',
          error instanceof Error ? error.message : 'No se pudo compartir el link de cobro.'
        );
      } finally {
        setLinkingCustomerId(null);
      }
    },
    [canEditCustomer, canCollect, canAdjustDebt, loadCustomers, paymentLinkAllowed, portalBaseUrl, refreshCustomerTotalCount]
  );

  const sendReminder = useCallback(
    async (customer: CustomerAccountRow) => {
      try {
        setLinkingCustomerId(customer.id);
        await sendCustomerWhatsAppReminder({
          customer,
          portalBaseUrl,
          canEdit: canEditCustomer || canCollect || canAdjustDebt,
          paymentLinkAllowed,
        });
      } catch (error: unknown) {
        reportUserVisibleFailure({ module: 'customers', op: 'customer.whatsapp_reminder', error, title: 'No se pudo abrir WhatsApp.' });
        console.warn('[Customer] Error al enviar recordatorio:', error);
        showError(
          'WhatsApp',
          error instanceof Error ? error.message : 'No se pudo abrir WhatsApp.'
        );
      } finally {
        setLinkingCustomerId(null);
      }
    },
    [canEditCustomer, canCollect, canAdjustDebt, paymentLinkAllowed, portalBaseUrl]
  );

  const detailModalProps = useMemo(
    () => ({
      visible: detailVisible,
      customer: detailCustomer,
      canEdit: canEditCustomer || canSetCreditLimit,
      canBlock,
      canCollect,
      canAdjustDebt,
      linkingCustomerId,
      formatMoney,
      onClose: () => {
        blockFlow.closeBlockConfirm();
        closeArchiveConfirm();
        closeCustomerDetail();
      },
      confirmOverlay: blockFlow.blockConfirmModalProps
        ? {
            ...blockFlow.blockConfirmModalProps,
            onClose: blockFlow.closeBlockConfirm,
            onConfirm: blockFlow.confirmToggleBlock,
          }
        : archiveNoteConfirmModalProps
          ? {
              ...archiveNoteConfirmModalProps,
              onClose: closeArchiveConfirm,
              onConfirm: confirmArchiveNote,
            }
          : null,
      onOpenMovements: (c: CustomerAccountRow) => {
        closeCustomerDetail();
        void movementFlow.openMovementModal(c);
      },
      onOpenHistory: (c: CustomerAccountRow) => {
        closeCustomerDetail();
        void historyFlow.openCustomerHistory(c);
      },
      onEdit: (c: CustomerAccountRow) => {
        closeCustomerDetail();
        editorFlow.openEditCustomerEditor(c);
      },
      onWhatsApp: (c: CustomerAccountRow) => void sendReminder(c),
      onShare: (c: CustomerAccountRow) => void sharePaymentLink(c),
      onToggleBlock: blockFlow.requestToggleBlock,
      onViewSale: (saleId: string) => {
        preserveCustomerDetailForShellNavigation();
        closeCustomerDetail();
        openSaleDetail(navigation, { saleId });
      },
      onCollectSale: (sale: { id: string; pendingAmount: number }) => {
        if (!detailCustomer) return;
        const customer = detailCustomer;
        closeCustomerDetail();
        void movementFlow.openMovementModal(customer, {
          saleId: sale.id,
          suggestedAmount: sale.pendingAmount,
        });
      },
      onExportStatement: (c: CustomerAccountRow) => void exportStatement(c),
      pendingSales,
      loadingPendingSales,
      agingBuckets,
      appointments: customerAppointments.appointments,
      appointmentsLoading: customerAppointments.loading,
      appointmentSummary: customerAppointments.summary,
      salesSummary: customerAppointments.salesSummary,
      salesWindows: customerAppointments.salesWindows,
      apptPage: customerAppointments.apptPage,
      apptHasMore: customerAppointments.apptHasMore,
      onLoadApptPage: customerAppointments.loadApptPage,
      customerSales: customerSales.sales,
      customerSalesLoading: customerSales.loading,
      salesPage: customerSales.page,
      salesHasMore: customerSales.hasMore,
      onLoadSalesPage: customerSales.loadPage,
      branches: branchOptions,
      serviceRecords: customerServiceRecords.serviceRecords,
      serviceRecordsLoading: customerServiceRecords.loading,
      customerNotes: customerServiceRecords.notes,
      customerNotesLoading: customerServiceRecords.loading,
      notesPage: customerServiceRecords.notesPage,
      notesHasMore: customerServiceRecords.notesHasMore,
      onLoadNotesPage: customerServiceRecords.loadNotesPage,
      onAddNote: canEditCustomer ? handleAddNote : undefined,
      onArchiveNote: canEditCustomer ? handleArchiveNote : undefined,
      customerTasks: customerTasks.tasks,
      customerTasksLoading: customerTasks.loading,
      tasksPage: customerTasks.page,
      tasksHasMore: customerTasks.hasMore,
      onLoadTasksPage: customerTasks.loadPage,
      onAddTask: canManageTasks ? handleAddTask : undefined,
      onCompleteTask: canManageTasks ? handleCompleteTask : undefined,
      onArchiveTask: canManageTasks ? handleArchiveTask : undefined,
    }),
    [
      agingBuckets,
      blockFlow,
      canEditCustomer,
      canSetCreditLimit,
      canCollect,
      canAdjustDebt,
      closeCustomerDetail,
      detailCustomer,
      detailVisible,
      editorFlow,
      exportStatement,
      pendingSales,
      formatMoney,
      historyFlow,
      linkingCustomerId,
      loadingPendingSales,
      movementFlow,
      navigation,
      sendReminder,
      sharePaymentLink,
      customerAppointments.appointments,
      customerAppointments.loading,
      customerAppointments.summary,
      customerAppointments.salesSummary,
      customerAppointments.salesWindows,
      customerAppointments.apptPage,
      customerAppointments.apptHasMore,
      customerAppointments.loadApptPage,
      customerSales.sales,
      customerSales.loading,
      customerSales.page,
      customerSales.hasMore,
      customerSales.loadPage,
      customerServiceRecords.serviceRecords,
      customerServiceRecords.notes,
      customerServiceRecords.loading,
      customerServiceRecords.notesPage,
      customerServiceRecords.notesHasMore,
      customerServiceRecords.loadNotesPage,
      handleAddNote,
      handleArchiveNote,
      canManageTasks,
      handleAddTask,
      handleCompleteTask,
      handleArchiveTask,
      customerTasks.tasks,
      customerTasks.loading,
      customerTasks.page,
      customerTasks.hasMore,
      customerTasks.loadPage,
      branchOptions,
      archiveNoteConfirmModalProps,
      closeArchiveConfirm,
      confirmArchiveNote,
    ]
  );

  return {
    detailCustomer,
    detailModalProps,
    openCustomerDetail,
    closeCustomerDetail,
    blockFlow,
  };
}
